import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import {
  filterSamplesByWeldNumber,
  parseWeldTxt,
  weldNumbersMatch
} from '../../utils/weldTxtParser'
import type { MachineType, WeldSample, WeldTxtParseResult } from '../../utils/weldTxtParser'
import {
  ALL_PARAMETERS,
  aggregateWeldParameters,
  type AggregateResult
} from '../../utils/weldParameterAggregator'
import { uploadWeldTelemetry } from '../../utils/weldTelemetryClient'
import { usePassMappingAutofill } from '../../utils/weldUpload/usePassMappingAutofill'
import PassMappingPanel from './weldUpload/PassMappingPanel'
import {
  parseWeldTxtFilename,
  type WeldTxtFilenameMeta
} from '../../utils/weldTxtFilenameParser'
import {
  applySlopeFilter,
  type SlopeFilterResult,
  type SlopeTimeConfig
} from '../../utils/weldSlopeFilter'
import type { EmbeddedFooterActions } from './weldUpload/embeddedFooterActions'
import { fileSelectionListKey } from '../../utils/weldUpload/mergeFileSelections'
import ConfirmDialog from '../ui/ConfirmDialog'
import { findUnmappedWpsPassLabels } from '../../utils/weldParametersEmptyPasses'
import {
  passMappingKeysFromRows,
  passMappingRowsFromSamples
} from '../../utils/weldUpload/passMappingKeys'

/**
 * Fallback slope-time window used when the caller doesn't pass an explicit
 * `slopeTime` prop. Mirrors `DEFAULT_SLOPE_TIME` on FloorSheet — kept in
 * sync by convention; both default to "4 sec in / 4 sec out" per the
 * Slope Time (sec) Measurement Settings tab.
 */
const DEFAULT_SLOPE_TIME: SlopeTimeConfig = { inSec: 4, outSec: 4 }

const MACHINE_OPTIONS: { value: MachineType; label: string }[] = [
  { value: 'p600z', label: 'P600Z' },
  { value: 'm500', label: 'M500' },
  { value: 'p625', label: 'P625' },
  { value: 'iwm', label: 'IWM' }
]

/**
 * The aggregator is given the full parameter set so the persisted blob covers
 * every parameter at upload time. Column visibility is a render-time concern,
 * handled by `weldParametersColumnLayout` + `ColumnSelectorDialog`.
 */
const UPLOAD_PARAMETERS = ALL_PARAMETERS

export interface ExistingWeldSummary {
  source: 'upload' | 'manual'
  /** Populated only when `source === 'upload'`. */
  sourceFilename?: string
  /** ISO-8601 timestamp. Populated only when `source === 'upload'`. */
  uploadedAt?: string
  /** Previous TXT pass-name → canonical label, used to pre-fill Rename Pass. */
  passMappings?: Record<string, string>
}

export interface UploadCompletePayload {
  aggregate: AggregateResult
  /**
   * Deprecated single-name fallback. Single-file uploads keep using the raw
   * TXT name; multi-file uploads compact the list into
   * `<first>.txt + N more`. Callers that need the full list should read
   * `sourceFilenames` instead.
   */
  sourceFilename: string
  /**
   * Full ordered list of TXT filenames whose samples were combined into the
   * uploaded batch. Always at least one entry. Mirrors the server's
   * `weld_telemetry_uploads.source_filenames` JSON column added in issue 05.
   */
  sourceFilenames: string[]
  passMappings: Record<string, string>
  /** ISO-8601 timestamp captured at the moment the POST succeeded. */
  uploadedAt: string
  /**
   * Tokens parsed from the source TXT filename (`14INCH ST1 CCW.txt` →
   * `{ pipeSize: 14, station: 1, direction: 'CCW' }`). For multi-file
   * batches, this is the parsed meta of the FIRST file — issue 07 owns the
   * mixed-station validation across the batch.
   */
  filenameMeta: WeldTxtFilenameMeta
  /** Discriminator when the upload source is not tab-separated telemetry TXT. */
  uploadFormat?: 'telemetry-txt' | 'alx-summary'
}

/**
 * One TXT file inside the multi-file batch. Tracked in selection order so
 * intra-batch sample dedup (issue 06) deterministically prefers earlier
 * files when two files declare the same `evtNo`.
 */
interface ParsedFileEntry {
  file: File
  parse: WeldTxtParseResult
}

type PreparedFileEntry = {
  file: File
  parse: WeldTxtParseResult
  /** Samples kept after the FloorSheet Weld ID row filter (or all when unlocked). */
  filteredSamples: WeldSample[]
  excludedSampleCount: number
  distinctWeldNumbers: string[]
}

interface UploadWeldDataDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  pipeSetId: string
  weldId: string
  /** Labels from `expandPassSequenceToLabels(WpsPassDetails)`. */
  wpsPassLabels: string[]
  onUploadComplete: (payload: UploadCompletePayload) => void
  /**
   * When this weld already has data (uploaded TXT or manual entry), the dialog
   * shows a warning banner and gates the POST behind a confirm-on-confirm.
   * Issue 07: Re-upload with warning.
   */
  existingSummary?: ExistingWeldSummary | null
  /**
   * Human-facing Weld # for the FloorSheet row the dialog was opened for.
   * Multi-file batches (issue 06) require every TXT's parsed `Weld #` to
   * match this value; mismatched batches disable Upload and surface a
   * routing hint pointing forward to issue 07.
   *
   * Optional for back-compat with callers that don't have it handy — in
   * that case the dialog falls back to "match all files' weld numbers
   * against each other" and silently allows the upload when they're
   * consistent.
   */
  currentWeldNumber?: string | null
  /**
   * Slope-in / slope-out window (whole seconds) configured per-weld via the
   * FloorSheet Measurement Settings → "Slope Time (sec)" tab. Samples whose
   * `recordedAt` falls within `inSec` of the earliest timestamp or within
   * `outSec` of the latest timestamp are dropped before aggregation and
   * before the server payload is built, so ramp-up / ramp-down samples
   * don't pollute the persisted min/avg/max stats. Defaults to
   * `{ inSec: 4, outSec: 4 }` when omitted.
   */
  slopeTime?: SlopeTimeConfig
  embedded?: boolean
  initialFiles?: File[]
  hideFilePicker?: boolean
  onFilesChange?: (files: File[]) => void
  onFooterActionsChange?: (actions: EmbeddedFooterActions | null) => void
}

export default function UploadWeldDataDialog({
  open,
  onClose,
  projectId,
  pipeSetId,
  weldId,
  wpsPassLabels,
  onUploadComplete,
  existingSummary,
  currentWeldNumber,
  slopeTime,
  embedded = false,
  initialFiles,
  hideFilePicker = false,
  onFilesChange,
  onFooterActionsChange
}: UploadWeldDataDialogProps) {
  const effectiveSlopeTime: SlopeTimeConfig = slopeTime ?? DEFAULT_SLOPE_TIME
  const [machineType, setMachineType] = useState<MachineType>('p600z')
  /**
   * The full batch of selected TXT files in selection order. Single-file
   * uploads simply land here as a length-1 array — preserving the pre-issue-06
   * happy path with no special-casing in the rest of the component.
   */
  const [parsedFiles, setParsedFiles] = useState<ParsedFileEntry[]>([])
  /**
   * Per-file routing decision (issue 07, scoped). Keyed by selection-order
   * index so file-list re-orderings stay consistent. Values:
   *   - `'attach'` (default) — include this file in the batch destined for
   *     the current weld.
   *   - `'skip'` — exclude this file from the upload entirely. Useful when
   *     a file's parsed `Weld #` disagrees with the current weld and the
   *     user wants to drop it rather than re-pick the batch.
   *
   * Full multi-destination routing (attach to another weld, create a new
   * weld, per-file pass-mapping panel) is deferred to a follow-up — those
   * pieces require FloorSheet section-write integration that's out of
   * scope here.
   */
  const [fileRoutes, setFileRoutes] = useState<Record<number, 'attach' | 'skip'>>({})
  /**
   * Indices that the auto-skip effect (driven by the Weld # picker) flipped
   * to `'skip'`. Tracked separately from manual user skips so we can RE-ATTACH
   * a file when the user later picks its Weld # without clobbering files the
   * user skipped by hand. Reset every time `parsedFiles` changes.
   */
  const autoSkippedIndicesRef = useRef<Set<number>>(new Set())
  /**
   * Indices the user has explicitly routed via the per-file Skip / Include
   * toggle. Once a user touches a file, the picker auto-skip effect will
   * NEVER overwrite that decision again — they keep total control over
   * mismatched-but-still-wanted files.
   */
  const manuallyRoutedIndicesRef = useRef<Set<number>>(new Set())
  /**
   * Currently selected Weld # for the upload batch. Drives the auto-skip
   * effect: every file whose `parse.weldNumber` differs gets routed to
   * `'skip'`. `null` while no files are loaded or no detected Weld #s exist.
   */
  const [chosenWeldNumber, setChosenWeldNumber] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [warningDismissed, setWarningDismissed] = useState(false)
  const [confirmPending, setConfirmPending] = useState(false)
  const [unmappedConfirmOpen, setUnmappedConfirmOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const lastInitialFilesKeyRef = useRef('')


  const expectedWeldId = (currentWeldNumber ?? '').trim()
  const floorSheetWeldLocked = expectedWeldId !== ''

  /**
   * The files that will actually contribute to the upload — everything
   * except files the user has explicitly skipped (issue 07 routing).
   */
  const activeFiles = useMemo<ParsedFileEntry[]>(() => {
    return parsedFiles.filter((_, index) => fileRoutes[index] !== 'skip')
  }, [parsedFiles, fileRoutes])

  const preparedActiveFiles = useMemo<PreparedFileEntry[]>(() => {
    return activeFiles.map((entry) => {
      if (entry.parse.error) {
        return {
          file: entry.file,
          parse: entry.parse,
          filteredSamples: [],
          excludedSampleCount: 0,
          distinctWeldNumbers: []
        }
      }
      const filterResult = filterSamplesByWeldNumber(
        entry.parse.samples,
        floorSheetWeldLocked ? expectedWeldId : ''
      )
      return {
        file: entry.file,
        parse: entry.parse,
        filteredSamples: filterResult.kept,
        excludedSampleCount: filterResult.excludedCount,
        distinctWeldNumbers:
          entry.parse.distinctWeldNumbers.length > 0
            ? entry.parse.distinctWeldNumbers
            : filterResult.distinctInFile
      }
    })
  }, [activeFiles, expectedWeldId, floorSheetWeldLocked])

  /**
   * Distinct, trimmed, non-empty `Weld #` tokens detected across every
   * non-erroring parsed file (row-level union). Used to drive the picker
   * dropdown when the FloorSheet Weld ID is not locked.
   */
  const detectedWeldNumbers = useMemo<string[]>(() => {
    const seen = new Set<string>()
    for (const entry of parsedFiles) {
      if (entry.parse.error) continue
      const distinct =
        entry.parse.distinctWeldNumbers.length > 0
          ? entry.parse.distinctWeldNumbers
          : entry.parse.weldNumber
            ? [entry.parse.weldNumber]
            : []
      for (const wn of distinct) {
        const trimmed = wn.trim()
        if (trimmed) seen.add(trimmed)
      }
    }
    return Array.from(seen).sort()
  }, [parsedFiles])

  const weldIdFilterSummary = useMemo(() => {
    if (!floorSheetWeldLocked || parsedFiles.length === 0) return null
    const detected = new Set<string>()
    let hasParseableFile = false
    for (const entry of parsedFiles) {
      if (entry.parse.error) continue
      hasParseableFile = true
      for (const wn of entry.parse.distinctWeldNumbers) {
        if (wn.trim()) detected.add(wn.trim())
      }
    }
    const detectedWeldNumbersSorted = Array.from(detected).sort()
    const excludedSampleCount = preparedActiveFiles.reduce(
      (sum, entry) => sum + entry.excludedSampleCount,
      0
    )
    const matchingSampleCount = preparedActiveFiles.reduce(
      (sum, entry) => sum + entry.filteredSamples.length,
      0
    )
    return {
      detectedWeldNumbers: detectedWeldNumbersSorted,
      excludedSampleCount,
      matchingSampleCount,
      hasExtraWeldIds: detectedWeldNumbersSorted.some(
        (wn) => !weldNumbersMatch(wn, expectedWeldId)
      ),
      noMatches: hasParseableFile && matchingSampleCount === 0
    }
  }, [floorSheetWeldLocked, parsedFiles, expectedWeldId, preparedActiveFiles])

  // Auto-seed `chosenWeldNumber` whenever the detected set changes. Prefer
  // the FloorSheet weld this dialog was opened for; otherwise default to
  // the first detected value. Resets to `null` when files are cleared so
  // a fresh selection re-seeds cleanly. Skipped when FloorSheet Weld ID is
  // locked — row-level filtering handles that case instead.
  useEffect(() => {
    if (floorSheetWeldLocked) {
      setChosenWeldNumber(expectedWeldId)
      return
    }
    if (detectedWeldNumbers.length === 0) {
      setChosenWeldNumber(null)
      return
    }
    setChosenWeldNumber((prev) => {
      if (prev !== null && detectedWeldNumbers.includes(prev)) return prev
      return detectedWeldNumbers[0]
    })
  }, [detectedWeldNumbers, expectedWeldId, floorSheetWeldLocked])

  // Auto-skip files whose internal Weld # disagrees with the picker's value.
  // Files matching the picked value get their auto-skip cleared. Files the
  // user has manually routed (`manuallyRoutedIndicesRef`) are left alone in
  // both directions — the picker never overwrites a user decision.
  useEffect(() => {
    if (floorSheetWeldLocked) return
    if (chosenWeldNumber === null) return
    const auto = autoSkippedIndicesRef.current
    const manual = manuallyRoutedIndicesRef.current
    setFileRoutes((prev) => {
      const next: Record<number, 'attach' | 'skip'> = { ...prev }
      let changed = false
      parsedFiles.forEach((entry, index) => {
        if (entry.parse.error) return
        if (manual.has(index)) return
        const wn = (entry.parse.weldNumber ?? '').trim()
        if (!wn) return
        if (!weldNumbersMatch(wn, chosenWeldNumber)) {
          if (next[index] !== 'skip') {
            next[index] = 'skip'
            auto.add(index)
            changed = true
          }
        } else if (auto.has(index)) {
          // The picker's pivot now MATCHES this file — re-attach it (clear
          // the auto-skip we previously applied). Files the user skipped
          // by hand stay skipped because their index isn't in `auto`.
          delete next[index]
          auto.delete(index)
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [chosenWeldNumber, parsedFiles])

  // Union of distinct Lead/Trail zone names across ACTIVE files (skipped files
  // don't contribute to Rename Pass either).
  const aggregatePassMappingRows = useMemo(() => {
    const samples: WeldSample[] = []
    for (const entry of preparedActiveFiles) {
      if (entry.parse.error) continue
      samples.push(...entry.filteredSamples)
    }
    return passMappingRowsFromSamples(samples)
  }, [preparedActiveFiles])

  const aggregatePassMappingKeys = useMemo(
    () => passMappingKeysFromRows(aggregatePassMappingRows),
    [aggregatePassMappingRows]
  )

  const {
    passMappings,
    setPassMappings,
    loading: passMappingsLoading,
    learnedPrefillCount
  } = usePassMappingAutofill({
    passNames: aggregatePassMappingKeys,
    wpsPassLabels,
    heuristicMode: 'telemetry',
    enabled: aggregatePassMappingKeys.length > 0
  })

  const unmappedWpsLabels = useMemo(
    () => findUnmappedWpsPassLabels(wpsPassLabels, passMappings),
    [wpsPassLabels, passMappings]
  )

  /**
   * Live preview of how the configured slope window will trim the staged
   * sample batch. We `applySlopeFilter` over the same unioned, deduped
   * sample set that `runUpload` builds so the chip's numbers match what
   * actually flows to the server.
   *
   * Side note: this also re-stamps `sample.sourceFileIndex` for every
   * active file so the dialog's mutation stays in sync if the user
   * reorders / un-skips files between previews. The stamping is
   * idempotent (same `activeFiles` → same indices), so the memo is still
   * pure with respect to its inputs.
   *
   * Dedup key is `${sourceFileIndex}:${evtNo}:${torch}` so two DIFFERENT TXTs
   * TXT export from the welder starts numbering at 1, so two distinct
   * files routinely overlap on `Evt #`. Using just evtNo as the key
   * would silently drop every row of every file after the first. The
   * per-file scope here only filters out true intra-file duplicates
   * (i.e. the same TXT picked twice in the file selector).
   */
  const slopePreview = useMemo<SlopeFilterResult>(() => {
    for (let fileIdx = 0; fileIdx < preparedActiveFiles.length; fileIdx++) {
      const entry = preparedActiveFiles[fileIdx]
      if (entry.parse.error) continue
      for (const sample of entry.filteredSamples) {
        sample.sourceFileIndex = fileIdx
      }
    }
    const unioned: WeldSample[] = []
    const seenKeys = new Set<string>()
    for (let fileIdx = 0; fileIdx < preparedActiveFiles.length; fileIdx++) {
      const entry = preparedActiveFiles[fileIdx]
      if (entry.parse.error) continue
      for (const sample of entry.filteredSamples) {
        const evt = typeof sample.evtNo === 'number' ? sample.evtNo : Number(sample.evtNo)
        if (!Number.isFinite(evt)) continue
        const key = `${fileIdx}:${evt}:${sample.torch}`
        if (seenKeys.has(key)) continue
        seenKeys.add(key)
        unioned.push(sample)
      }
    }
    return applySlopeFilter(unioned, effectiveSlopeTime)
  }, [preparedActiveFiles, effectiveSlopeTime])

  /**
   * Detect cross-file Weld # disagreements among ACTIVE files. Skipped
   * files don't count toward the mismatch — that's the whole point of the
   * issue 07 routing toggle.
   *
   * When the multi-ID picker is in play (`detectedWeldNumbers.length >= 2`)
   * the mismatch is suppressed entirely — the picker IS the user's
   * declared intent, even if it disagrees with the FloorSheet's
   * `currentWeldNumber`. Tearing down the gating here lets the user
   * proceed to upload after picking; the auto-skip effect already filters
   * the batch down to files that match the picker.
   */
  const weldMismatch = useMemo<{
    expected: string | null
    actuals: string[]
    /** Indices (into `parsedFiles`) of files involved in the conflict. */
    conflictingIndices: number[]
  } | null>(() => {
    if (floorSheetWeldLocked) return null
    if (activeFiles.length === 0) return null
    const actuals: string[] = []
    const conflictingIndices: number[] = []
    parsedFiles.forEach((entry, index) => {
      if (fileRoutes[index] === 'skip') return
      const wn = (entry.parse.weldNumber ?? '').trim()
      if (wn === '') return
      if (!actuals.includes(wn)) actuals.push(wn)
    })
    const isMismatch =
      actuals.length > 1 ||
      (actuals.length === 1 &&
        currentWeldNumber !== undefined &&
        currentWeldNumber !== null &&
        currentWeldNumber.trim() !== '' &&
        !weldNumbersMatch(actuals[0], currentWeldNumber.trim()))
    if (!isMismatch) return null
    // Multi-ID picker is in charge — don't block uploads on the
    // FloorSheet/file disagreement. The single-ID-mismatch banner still
    // surfaces below for the (single file, wrong Weld #) case.
    if (detectedWeldNumbers.length >= 2) return null
    const expectedTrimmed = currentWeldNumber?.trim() || null
    parsedFiles.forEach((entry, index) => {
      if (fileRoutes[index] === 'skip') return
      const wn = (entry.parse.weldNumber ?? '').trim()
      if (wn === '') return
      if (expectedTrimmed && !weldNumbersMatch(wn, expectedTrimmed)) {
        conflictingIndices.push(index)
      } else if (!expectedTrimmed && !weldNumbersMatch(wn, actuals[0])) {
        conflictingIndices.push(index)
      }
    })
    return { expected: expectedTrimmed, actuals, conflictingIndices }
  }, [
    parsedFiles,
    fileRoutes,
    activeFiles.length,
    currentWeldNumber,
    detectedWeldNumbers.length,
    floorSheetWeldLocked
  ])

  // Reset confirm/dismiss state when the dialog re-opens for a different weld.
  useEffect(() => {
    if (!open) {
      setWarningDismissed(false)
      setConfirmPending(false)
      lastInitialFilesKeyRef.current = ''
    }
  }, [open])

  useEffect(() => {
    if (!embedded || !initialFiles) return
    if (initialFiles.length === 0) {
      lastInitialFilesKeyRef.current = ''
      setParsedFiles([])
      return
    }
    const nextKey = fileSelectionListKey(initialFiles)
    if (nextKey === lastInitialFilesKeyRef.current) return
    lastInitialFilesKeyRef.current = nextKey
    handleFiles(initialFiles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, initialFiles])

  const anyActiveFileError = activeFiles.some((entry) => entry.parse.error)
  const allMapped =
    activeFiles.length > 0 &&
    !anyActiveFileError &&
    !passMappingsLoading &&
    aggregatePassMappingKeys.every((p) => (passMappings[p] ?? '') !== '')

  const hasMatchingSamples = floorSheetWeldLocked
    ? (weldIdFilterSummary?.matchingSampleCount ?? 0) > 0
    : activeFiles.some((entry) => !entry.parse.error && entry.parse.samples.length > 0)
  const canUpload = allMapped && !busy && hasMatchingSamples && weldMismatch === null

  const handleUploadClickRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!embedded || !onFooterActionsChange || !open) return
    onFooterActionsChange({
      canUpload,
      busy,
      label: 'Upload',
      onUpload: () => handleUploadClickRef.current()
    })
    return () => onFooterActionsChange(null)
  }, [embedded, onFooterActionsChange, open, canUpload, busy])

  if (!open) return null

  const hasNoPassesDefined = wpsPassLabels.length === 0

  const handleFiles = (selected: File[]) => {
    setServerError(null)
    setFileRoutes({})
    autoSkippedIndicesRef.current = new Set()
    manuallyRoutedIndicesRef.current = new Set()
    if (selected.length === 0) {
      setParsedFiles([])
      return
    }
    // Read every selected file in parallel; the first onload to complete
    // does NOT win — we wait for ALL via Promise.all so the resulting
    // `parsedFiles` array preserves the selection order. Read errors are
    // synthesised into a stub parse result with `error` set so the file
    // row can render a per-file warning instead of silently dropping it.
    const readers = selected.map(
      (file) =>
        new Promise<ParsedFileEntry>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => {
            const text = typeof reader.result === 'string' ? reader.result : ''
            resolve({ file, parse: parseWeldTxt(text, machineType) })
          }
          reader.onerror = () => {
            resolve({
              file,
              parse: {
                samples: [],
                distinctPassNames: [],
                distinctWeldNumbers: [],
                jobNumber: '',
                weldNumber: '',
                totalRows: 0,
                error: 'Could not read this file. Please pick the TXT file again.'
              }
            })
          }
          reader.readAsText(file)
        })
    )
    Promise.all(readers).then((entries) => {
      setParsedFiles(entries)
      if (!embedded) {
        onFilesChange?.(entries.map((e) => e.file))
      }
    })
  }

  const proceedAfterMappingChecks = () => {
    if (existingSummary) {
      setConfirmPending(true)
      return
    }
    void runUpload()
  }

  const handleUploadClick = () => {
    if (!canUpload) return
    if (unmappedWpsLabels.length > 0) {
      setUnmappedConfirmOpen(true)
      return
    }
    proceedAfterMappingChecks()
  }

  handleUploadClickRef.current = handleUploadClick

  const runUpload = async () => {
    if (preparedActiveFiles.length === 0 || anyActiveFileError || !hasMatchingSamples) return
    setBusy(true)
    setServerError(null)
    try {
      // Build the outgoing mapping from the user's state, dropping any Unmapped entries
      // (canUpload guards against this, but stay defensive in case of stale state).
      const userMappings: Record<string, string> = {}
      for (const name of aggregatePassMappingKeys) {
        const chosen = passMappings[name]
        if (chosen) userMappings[name] = chosen
      }

      // Stamp every sample with its source-file index BEFORE the dedup
      // loop so the surviving sample carries the correct origin. The
      // server persists this in `weld_telemetry_samples.source_file_index`
      // (migration 030) and the Pass Analytics table uses it to render
      // dividers between rows from different files.
      for (let fileIdx = 0; fileIdx < preparedActiveFiles.length; fileIdx++) {
        for (const sample of preparedActiveFiles[fileIdx].filteredSamples) {
          sample.sourceFileIndex = fileIdx
        }
      }
      // Union all samples across the ACTIVE files. Dedup key is
      // `${fileIdx}:${evtNo}:${torch}` so two DIFFERENT TXTs that happen to
      // overlap on `Evt #` (the welder restarts evt numbering at 1
      // for every export) both contribute their full sample set —
      // each file's evts only need to be unique WITHIN that file.
      // Without this, the second/third/... file in a multi-file batch
      // would be dropped entirely whenever the welder produced
      // overlapping evt ranges (which is essentially always).
      const unionedSamples: WeldSample[] = []
      const seenKeys = new Set<string>()
      for (let fileIdx = 0; fileIdx < preparedActiveFiles.length; fileIdx++) {
        const entry = preparedActiveFiles[fileIdx]
        for (const sample of entry.filteredSamples) {
          const evt = typeof sample.evtNo === 'number' ? sample.evtNo : Number(sample.evtNo)
          if (!Number.isFinite(evt)) continue
          const key = `${fileIdx}:${evt}:${sample.torch}`
          if (seenKeys.has(key)) continue
          seenKeys.add(key)
          unionedSamples.push(sample)
        }
      }
      // Apply the per-weld Slope Time (sec) window BEFORE we POST or
      // aggregate. `applySlopeFilter` is a no-op when both inSec/outSec
      // are zero, and falls back to "keep all" when the window is wider
      // than the recorded duration (windowExceedsDuration).
      const slopeResult = applySlopeFilter(unionedSamples, effectiveSlopeTime)
      const samplesForUpload = slopeResult.kept
      const sourceFilenames = preparedActiveFiles.map((entry) => entry.file.name)
      const isMultiFile = sourceFilenames.length > 1
      // Multi-file batches always merge so two batches sent back-to-back
      // accumulate rather than clobber. Single-file uploads keep today's
      // replace semantics so the per-weld "re-upload replaces everything"
      // contract stays intact.
      const mode: 'replace' | 'merge' = isMultiFile ? 'merge' : 'replace'
      const sourceFilenameLabel = isMultiFile
        ? `${sourceFilenames[0]} + ${sourceFilenames.length - 1} more`
        : sourceFilenames[0]

      // Pick the most informative job/weld number across the batch (first
      // non-empty). The server keeps the existing values for merge mode
      // when these come back empty.
      const firstNonEmpty = (selector: (entry: PreparedFileEntry) => string | null | undefined) => {
        for (const entry of preparedActiveFiles) {
          const value = selector(entry)
          if (value && String(value).trim() !== '') return String(value).trim()
        }
        return null
      }
      const jobNumber = firstNonEmpty((entry) => entry.parse.jobNumber)
      const weldNumber = floorSheetWeldLocked
        ? expectedWeldId
        : firstNonEmpty((entry) => entry.parse.weldNumber)

      await uploadWeldTelemetry({
        projectId,
        pipeSetId,
        weldId,
        machineType,
        samples: samplesForUpload,
        passMappings: userMappings,
        sourceFilename: sourceFilenameLabel,
        sourceFilenames,
        mode,
        jobNumber,
        weldNumber
      })

      const aggregate = aggregateWeldParameters(
        samplesForUpload,
        userMappings,
        UPLOAD_PARAMETERS
      )
      onUploadComplete({
        aggregate,
        sourceFilename: sourceFilenameLabel,
        sourceFilenames,
        passMappings: userMappings,
        uploadedAt: new Date().toISOString(),
        filenameMeta: parseWeldTxtFilename(sourceFilenames[0])
      })
      setConfirmPending(false)
      onClose()
    } catch (err: any) {
      setServerError(err?.message || 'Upload failed. Please try again.')
      setConfirmPending(false)
    } finally {
      setBusy(false)
    }
  }

  const passCount = aggregatePassMappingKeys.length
  const activeSampleCount = preparedActiveFiles.reduce(
    (sum, entry) => sum + entry.filteredSamples.length,
    0
  )
  const totalParsedSampleCount = parsedFiles.reduce(
    (sum, entry) => sum + (entry.parse.error ? 0 : entry.parse.samples.length),
    0
  )
  const skippedCount = parsedFiles.length - activeFiles.length

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    if (busy || hasNoPassesDefined) return
    setDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setDragOver(false)
    if (busy || hasNoPassesDefined) return
    const dropped = Array.from(event.dataTransfer.files ?? [])
    if (dropped.length > 0) handleFiles(dropped)
  }

  const handleClearAll = () => {
    if (busy) return
    setParsedFiles([])
    setFileRoutes({})
    autoSkippedIndicesRef.current = new Set()
    manuallyRoutedIndicesRef.current = new Set()
    setServerError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveOne = (index: number) => {
    if (busy) return
    setServerError(null)
    setParsedFiles((prev) => prev.filter((_, i) => i !== index))
    // Shift route indices down past the removed slot so the per-file map
    // stays aligned with the new array.
    setFileRoutes((prev) => {
      const next: Record<number, 'attach' | 'skip'> = {}
      for (const [key, value] of Object.entries(prev)) {
        const numericKey = Number(key)
        if (numericKey === index) continue
        next[numericKey > index ? numericKey - 1 : numericKey] = value
      }
      return next
    })
    // Mirror the same shift on every per-index ref so subsequent picker
    // effect runs continue to recognise which skips it owned and which
    // files the user touched by hand. Uses `Set.forEach` rather than
    // `for...of` because the project's TS target is below ES2015 and
    // `--downlevelIteration` is off (TS2802 otherwise).
    const shiftIndexSet = (set: Set<number>): Set<number> => {
      const next = new Set<number>()
      set.forEach((value) => {
        if (value === index) return
        next.add(value > index ? value - 1 : value)
      })
      return next
    }
    autoSkippedIndicesRef.current = shiftIndexSet(autoSkippedIndicesRef.current)
    manuallyRoutedIndicesRef.current = shiftIndexSet(manuallyRoutedIndicesRef.current)
    if (fileInputRef.current) {
      // Resetting the underlying input lets the user re-select the same
      // file later if they change their mind — browsers ignore an
      // identical-name change event otherwise.
      fileInputRef.current.value = ''
    }
  }

  const handleToggleSkip = (index: number) => {
    if (busy) return
    setServerError(null)
    // The user is manually overriding routing for this file. Promote the
    // index out of the picker's auto-skip set AND record the manual touch
    // so future picker effect runs leave it alone — the user wins for
    // the lifetime of this batch.
    autoSkippedIndicesRef.current.delete(index)
    manuallyRoutedIndicesRef.current.add(index)
    setFileRoutes((prev) => {
      const next = { ...prev }
      if (next[index] === 'skip') delete next[index]
      else next[index] = 'skip'
      return next
    })
  }

  const dialogCard = (
      <div
        className={`relative flex w-full flex-col overflow-hidden ${
          embedded
            ? 'max-h-none rounded-lg border border-gray-200 bg-white'
            : 'max-h-[90vh] max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-large'
        }`}
      >
        {!embedded ? (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2
              id="upload-weld-data-title"
              className="font-heading text-lg font-semibold text-blueprint-blue"
            >
              Upload Weld Data
            </h2>
            <p className="text-xs text-gray-500">
              Import a welding TXT log and map each pass to a WPS label.
            </p>
          </div>
          {hasNoPassesDefined ? null : (
            <button
              type="button"
              data-testid="upload-weld-data-header-upload"
              className="inline-flex items-center gap-1.5 rounded-md bg-hero-red px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-hero-red-60 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleUploadClick}
              disabled={!canUpload}
            >
              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          )}
          <button
            type="button"
            className="-mr-2 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        ) : null}

        {hasNoPassesDefined ? (
          <div className="flex-1 overflow-y-auto px-6 py-8" data-testid="no-passes-guard">
            <div className="flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-4 text-sm text-warning-800">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-warning-600" />
              <div>
                <p className="font-semibold">No passes defined yet</p>
                <p className="mt-1 text-xs leading-relaxed text-warning-700">
                  Create passes in Pass Details before uploading weld data.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative flex-1 space-y-5 overflow-y-auto bg-page-bg/40 px-6 py-5">
            {busy ? (
              <div
                data-testid="upload-progress-overlay"
                className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-[1px]"
              >
                <p className="text-sm font-semibold text-blueprint-blue">Uploading TXT…</p>
                <div
                  role="progressbar"
                  aria-label="Uploading"
                  data-testid="upload-progress-bar"
                  className="relative h-1.5 w-56 overflow-hidden rounded-full bg-gray-200"
                >
                  <div className="absolute inset-y-0 w-1/3 animate-progress-indeterminate rounded-full bg-hero-red" />
                </div>
                <p className="text-[11px] text-heritage-grey">
                  Hold tight while we send your weld data.
                </p>
              </div>
            ) : null}
            {existingSummary && !warningDismissed ? (
              <ReuploadWarningBanner
                summary={existingSummary}
                onDismiss={() => setWarningDismissed(true)}
              />
            ) : null}

            <div>
              <label
                htmlFor="machine-type-select"
                className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-600"
              >
                Machine type
              </label>
              <select
                id="machine-type-select"
                className="w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                value={machineType}
                onChange={(e) => setMachineType(e.target.value as MachineType)}
                disabled={busy}
              >
                {MACHINE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {detectedWeldNumbers.length >= 2 && !floorSheetWeldLocked ? (
              <div data-testid="weld-id-picker-row">
                <label
                  htmlFor="weld-id-picker"
                  className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-600"
                >
                  Use Weld #
                </label>
                <select
                  id="weld-id-picker"
                  data-testid="weld-id-picker"
                  className="w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  value={chosenWeldNumber ?? ''}
                  onChange={(e) => setChosenWeldNumber(e.target.value)}
                  disabled={busy}
                >
                  {detectedWeldNumbers.map((wn) => (
                    <option key={wn} value={wn}>
                      Weld {wn}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-heritage-grey">
                  The selected files contain multiple Weld # values. Pick the one to upload — files
                  with a different Weld # are skipped automatically. Use the per-file{' '}
                  <strong>Skip</strong> / <strong>Include</strong> button to override on a single
                  file.
                </p>
              </div>
            ) : null}

            <div>
              {!hideFilePicker ? (
              <>
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                Welding TXT file{parsedFiles.length > 1 ? 's' : ''}
              </span>
              {parsedFiles.length > 0 ? (
                <div
                  data-testid="upload-selected-file"
                  className="space-y-2"
                >
                  {parsedFiles.map((entry, index) => {
                    const parseErr = entry.parse.error
                    const totalSampleCount = entry.parse.samples.length
                    const isSkipped = fileRoutes[index] === 'skip'
                    const fileDistinctWelds = entry.parse.distinctWeldNumbers
                    const activeParsedIndex = parsedFiles
                      .slice(0, index)
                      .filter((_, i) => fileRoutes[i] !== 'skip').length
                    const preparedEntry = isSkipped
                      ? undefined
                      : preparedActiveFiles[activeParsedIndex]
                    const keptSampleCount = isSkipped
                      ? 0
                      : preparedEntry?.filteredSamples.length ?? totalSampleCount
                    const excludedInFile = isSkipped ? 0 : preparedEntry?.excludedSampleCount ?? 0
                    const hasExtraWeldsInFile =
                      floorSheetWeldLocked &&
                      !parseErr &&
                      !isSkipped &&
                      fileDistinctWelds.some((wn) => !weldNumbersMatch(wn, expectedWeldId))
                    const noMatchesInFile =
                      floorSheetWeldLocked &&
                      !parseErr &&
                      !isSkipped &&
                      totalSampleCount > 0 &&
                      keptSampleCount === 0
                    const fileWeld = entry.parse.weldNumber
                    const expectedWeld = expectedWeldId
                    const weldDisagrees =
                      !floorSheetWeldLocked &&
                      !parseErr &&
                      expectedWeld !== '' &&
                      typeof fileWeld === 'string' &&
                      fileWeld.trim() !== '' &&
                      !weldNumbersMatch(fileWeld, expectedWeld)
                    const rowWarns = hasExtraWeldsInFile || noMatchesInFile || weldDisagrees
                    return (
                      <div
                        key={`${entry.file.name}-${index}`}
                        data-testid={`upload-file-row-${index}`}
                        role={parseErr ? 'alert' : undefined}
                        className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-xs shadow-sm ${
                          parseErr
                            ? 'border-error-200 bg-error-50 text-error-800'
                            : isSkipped
                              ? 'border-gray-200 bg-gray-50 text-gray-500 opacity-70'
                              : rowWarns
                                ? 'border-warning-200 bg-warning-50 text-warning-800'
                                : 'border-gray-200 bg-white text-gray-700'
                        }`}
                      >
                        <div
                          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ${
                            parseErr
                              ? 'bg-error-100 text-error-600'
                              : isSkipped
                                ? 'bg-gray-100 text-gray-400'
                                : rowWarns
                                  ? 'bg-warning-100 text-warning-600'
                                  : 'bg-primary-50 text-primary-600'
                          }`}
                        >
                          {parseErr || rowWarns ? (
                            <ExclamationTriangleIcon className="h-5 w-5" />
                          ) : (
                            <DocumentTextIcon className="h-5 w-5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={`truncate text-sm font-semibold ${
                              parseErr
                                ? 'text-error-800'
                                : isSkipped
                                  ? 'text-gray-500 line-through'
                                  : 'text-gray-800'
                            }`}
                          >
                            {entry.file.name}
                          </p>
                          <p
                            className={`text-[11px] ${
                              parseErr
                                ? 'text-error-700'
                                : rowWarns
                                  ? 'text-warning-700'
                                  : 'text-gray-500'
                            }`}
                          >
                            {parseErr
                              ? parseErr
                              : isSkipped
                                ? `Skipped — ${totalSampleCount.toLocaleString()} samples will not be uploaded`
                                : noMatchesInFile
                                  ? `${totalSampleCount.toLocaleString()} samples · Weld # ${fileDistinctWelds.join(', ') || fileWeld} — none match FloorSheet Weld ID ${expectedWeld}`
                                  : hasExtraWeldsInFile
                                    ? `${keptSampleCount.toLocaleString()} of ${totalSampleCount.toLocaleString()} samples kept · found Weld # ${fileDistinctWelds.join(', ')} (${excludedInFile.toLocaleString()} excluded)`
                                    : weldDisagrees
                                      ? `${totalSampleCount.toLocaleString()} samples · targets Weld ${fileWeld} (this dialog is open for Weld ${expectedWeld})`
                                      : `${keptSampleCount.toLocaleString()} sample${keptSampleCount === 1 ? '' : 's'}${
                                          fileDistinctWelds.length > 0
                                            ? ` · Weld # ${fileDistinctWelds.join(', ')}`
                                            : fileWeld
                                              ? ` · Weld ${fileWeld}`
                                              : ''
                                        }`}
                          </p>
                        </div>
                        {!parseErr ? (
                          <button
                            type="button"
                            data-testid={`upload-skip-file-${index}`}
                            aria-pressed={isSkipped}
                            className={`mr-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                              isSkipped
                                ? 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => handleToggleSkip(index)}
                            disabled={busy}
                          >
                            {isSkipped ? 'Include' : 'Skip'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          data-testid={`upload-remove-file-${index}`}
                          aria-label="Remove file"
                          className="-mr-1 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                          onClick={() => handleRemoveOne(index)}
                          disabled={busy}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  })}
                  <div className="flex items-center justify-between gap-3 px-1 text-[11px] text-gray-500">
                    <span data-testid="upload-batch-summary">
                      {parsedFiles.length} file{parsedFiles.length === 1 ? '' : 's'}
                      {skippedCount > 0 ? ` (${skippedCount} skipped)` : ''} ·{' '}
                      {activeSampleCount.toLocaleString()} sample{activeSampleCount === 1 ? '' : 's'}
                      {floorSheetWeldLocked &&
                      weldIdFilterSummary &&
                      weldIdFilterSummary.excludedSampleCount > 0
                        ? ` (${weldIdFilterSummary.excludedSampleCount.toLocaleString()} excluded by Weld ID filter)`
                        : ''}{' '}
                      · {passCount}{' '}
                      distinct pass{passCount === 1 ? '' : 'es'}
                    </span>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="weld-txt-file"
                        className="cursor-pointer rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                      >
                        Add more
                      </label>
                      <button
                        type="button"
                        data-testid="upload-clear-file"
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                        onClick={handleClearAll}
                        disabled={busy}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    id="weld-txt-file"
                    type="file"
                    accept=".txt,text/plain"
                    multiple
                    className="sr-only"
                    onChange={(e) => {
                      const added = Array.from(e.target.files ?? [])
                      if (added.length === 0) return
                      // Append rather than replace so the "Add more" affordance
                      // keeps prior selections.
                      const existingFiles = parsedFiles.map((entry) => entry.file)
                      handleFiles([...existingFiles, ...added])
                    }}
                    disabled={busy}
                  />
                </div>
              ) : (
                <label
                  htmlFor="weld-txt-file"
                  data-testid="upload-dropzone"
                  data-dragover={dragOver ? 'true' : undefined}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                    dragOver
                      ? 'border-hero-red bg-hero-red-10/40'
                      : 'border-gray-300 bg-white hover:border-hero-red hover:bg-hero-red-10/30'
                  } ${busy ? 'pointer-events-none opacity-60' : ''}`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-hero-red-10 text-hero-red">
                    <CloudArrowUpIcon className="h-6 w-6" aria-hidden />
                  </span>
                  <span className="text-sm font-semibold text-blueprint-blue">
                    Click to choose TXT file(s) or drag &amp; drop here
                  </span>
                  <span className="text-[11px] text-heritage-grey">
                    Accepted: .txt · multiple files allowed
                  </span>
                  <input
                    ref={fileInputRef}
                    id="weld-txt-file"
                    type="file"
                    accept=".txt,text/plain"
                    multiple
                    className="sr-only"
                    onChange={(e) => handleFiles(Array.from(e.target.files ?? []))}
                    disabled={busy}
                  />
                </label>
              )}
              </>
              ) : parsedFiles.length > 0 ? (
                <p className="text-[11px] text-gray-500" data-testid="upload-batch-summary">
                  {parsedFiles.length} file{parsedFiles.length === 1 ? '' : 's'} selected ·{' '}
                  {activeSampleCount.toLocaleString()} sample{activeSampleCount === 1 ? '' : 's'}
                </p>
              ) : null}
            </div>

            {floorSheetWeldLocked && weldIdFilterSummary?.noMatches ? (
              <p
                role="alert"
                data-testid="upload-weld-id-filter"
                className="text-[11px] text-error-600"
              >
                No matching Weld ID <strong>{expectedWeldId}</strong> (found:{' '}
                {weldIdFilterSummary.detectedWeldNumbers.join(', ') || '—'}).
              </p>
            ) : floorSheetWeldLocked &&
              weldIdFilterSummary &&
              (weldIdFilterSummary.hasExtraWeldIds || weldIdFilterSummary.excludedSampleCount > 0) ? (
              <p
                role="status"
                data-testid="upload-weld-id-filter"
                className="text-[11px] text-warning-700"
              >
                Using Weld ID <strong>{expectedWeldId}</strong> —{' '}
                {weldIdFilterSummary.excludedSampleCount.toLocaleString()} sample
                {weldIdFilterSummary.excludedSampleCount === 1 ? '' : 's'} excluded.
              </p>
            ) : null}

            {weldMismatch ? (
              <p
                role="alert"
                data-testid="upload-weld-mismatch"
                className="text-[11px] text-warning-700"
              >
                {weldMismatch.expected
                  ? `File Weld # (${weldMismatch.actuals.join(', ')}) does not match Weld ${weldMismatch.expected}. Skip or remove the file.`
                  : 'File Weld # could not be matched. Skip or remove the file.'}
              </p>
            ) : null}

            {parsedFiles.length > 0 &&
            !anyActiveFileError &&
            (effectiveSlopeTime.inSec > 0 || effectiveSlopeTime.outSec > 0) &&
            slopePreview.windowExceedsDuration ? (
              <p
                role="status"
                data-testid="slope-filter-warning"
                className="text-[11px] text-warning-700"
              >
                Slope window ({effectiveSlopeTime.inSec}s + {effectiveSlopeTime.outSec}s) exceeds
                weld duration — all samples kept.
              </p>
            ) : null}

            {parsedFiles.length > 0 && aggregatePassMappingKeys.length > 0 ? (
              <PassMappingPanel
                mappingRows={aggregatePassMappingRows}
                passMappings={passMappings}
                wpsPassLabels={wpsPassLabels}
                sourceLabel="Zone name"
                loading={passMappingsLoading}
                learnedPrefillCount={learnedPrefillCount}
                onMappingChange={(raw, canonical) =>
                  setPassMappings((prev) => ({ ...prev, [raw]: canonical }))
                }
                disabled={busy}
              />
            ) : null}

            {serverError ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 px-3 py-2.5 text-xs text-error-700"
              >
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-error-600" />
                <span>{serverError}</span>
              </div>
            ) : null}
          </div>
        )}

        {!embedded ? (
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-white px-6 py-3">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
        ) : null}

        {confirmPending ? (
          <ReuploadConfirmOverlay
            busy={busy}
            onCancel={() => setConfirmPending(false)}
            onConfirm={() => void runUpload()}
          />
        ) : null}

        <ConfirmDialog
          open={unmappedConfirmOpen}
          title="Some WPS passes are not mapped"
          description={`The following passes are not mapped to any zone pass: ${unmappedWpsLabels.join(', ')}. They will appear empty in Welding Parameters. Continue anyway?`}
          confirmLabel="Upload"
          cancelLabel="Go back"
          confirmBusy={busy}
          confirmBusyLabel="Uploading…"
          onConfirm={() => {
            setUnmappedConfirmOpen(false)
            proceedAfterMappingChecks()
          }}
          onCancel={() => setUnmappedConfirmOpen(false)}
        />
      </div>
  )

  const content = embedded ? (
    <div data-testid="upload-weld-data-embedded">{dialogCard}</div>
  ) : (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-blueprint-blue/40 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-weld-data-title"
    >
      {dialogCard}
    </div>
  )

  if (embedded) {
    if (!open) return null
    return content
  }

  return createPortal(content, document.body)
}

function ReuploadWarningBanner({
  summary,
  onDismiss
}: {
  summary: ExistingWeldSummary
  onDismiss: () => void
}) {
  const message =
    summary.source === 'upload'
      ? buildUploadWarningText(summary.sourceFilename, summary.uploadedAt)
      : 'Re-upload will overwrite manual edits for this weld.'

  return (
    <div
      data-testid="reupload-warning"
      role="status"
      className="flex items-center gap-2 text-[11px] text-warning-700"
    >
      <p className="flex-1">{message}</p>
      <button
        type="button"
        aria-label="Dismiss warning"
        className="rounded-md p-0.5 text-warning-700 transition-colors hover:bg-warning-50"
        onClick={onDismiss}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

function buildUploadWarningText(
  filename: string | undefined,
  uploadedAt: string | undefined
): string {
  const safeName = filename || 'an earlier upload'
  const formattedDate = formatUploadDate(uploadedAt)
  const dateChunk = formattedDate ? ` (${formattedDate})` : ''
  return `Existing upload from ${safeName}${dateChunk} will be replaced.`
}

function formatUploadDate(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  try {
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return d.toISOString()
  }
}

function ReuploadConfirmOverlay({
  busy,
  onCancel,
  onConfirm
}: {
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-blueprint-blue/40 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reupload-confirm-title"
      data-testid="reupload-confirm"
    >
      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-5 shadow-large">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-error-50 text-error-600 ring-1 ring-inset ring-error-100">
            <ExclamationTriangleIcon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 id="reupload-confirm-title" className="text-sm font-semibold text-gray-900">
              Replace existing weld data?
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              Replace existing weld data for this weld? This cannot be undone.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            data-testid="reupload-confirm-cancel"
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="reupload-confirm-confirm"
            className="inline-flex items-center justify-center rounded-md bg-error-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-error-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Replacing…' : 'Replace'}
          </button>
        </div>
      </div>
    </div>
  )
}
